import { gql } from "@/graphql/typed-gql"

import { PLACEMENT_TEST_FIELDS } from "@/graphql/queries/students"

export const CREATE_STUDENT = gql`
  mutation CreateStudent(
    $name: String!
    $birthYear: Int
    $birthMonth: Int
    $birthDay: Int
    $firstGradeYear: Int
    $notes: String
    $defaultExerciseCount: Int
    $subjectExerciseCounts: [SubjectExerciseCountInput!]
    $requireDiscussionResolution: Boolean
    $email: String
    $password: String
  ) {
    createStudent(
      name: $name
      birthYear: $birthYear
      birthMonth: $birthMonth
      birthDay: $birthDay
      firstGradeYear: $firstGradeYear
      notes: $notes
      defaultExerciseCount: $defaultExerciseCount
      subjectExerciseCounts: $subjectExerciseCounts
      requireDiscussionResolution: $requireDiscussionResolution
      email: $email
      password: $password
    ) {
      student {
        id
        name
        age
        birthYear
        birthMonth
        birthDay
        firstGradeYear
        grade
        notes
        defaultExerciseCount
        # Their address is asserted until they follow the link — so they are in no
        # family yet and cannot sign in. The list badges it; the toast below says
        # it once, at the moment somebody typed the address.
        emailPending
      }
      errors
      # Whether the confirmation mail actually left. The learner exists either
      # way, so a false here has to be said out loud — otherwise a grown-up waits
      # for a confirmation that was never sent.
      emailed
    }
  }
`

// The four birth/school numbers are sent on every save, as a number or as an
// explicit null — null is how the server is told a field was emptied, which is
// why there are no `clearX` flags any more.
export const UPDATE_STUDENT = gql`
  mutation UpdateStudent(
    $id: ID!
    $name: String
    $birthYear: Int
    $birthMonth: Int
    $birthDay: Int
    $firstGradeYear: Int
    $notes: String
    $defaultExerciseCount: Int
    $subjectExerciseCounts: [SubjectExerciseCountInput!]
    $requireDiscussionResolution: Boolean
    $email: String
    $password: String
  ) {
    updateStudent(
      id: $id
      name: $name
      birthYear: $birthYear
      birthMonth: $birthMonth
      birthDay: $birthDay
      firstGradeYear: $firstGradeYear
      notes: $notes
      defaultExerciseCount: $defaultExerciseCount
      subjectExerciseCounts: $subjectExerciseCounts
      requireDiscussionResolution: $requireDiscussionResolution
      email: $email
      password: $password
    ) {
      student {
        id
        name
        age
        birthYear
        birthMonth
        birthDay
        firstGradeYear
        grade
        gradeKnown
        notes
        defaultExerciseCount
        suggestedExerciseCount
        # Answered back so the form it was typed into re-reads the saved limits
        # rather than the ones it was rendered with.
        subjectSessionLengths {
          subjectKey
          subjectName
          count
          effectiveCount
        }
        requireDiscussionResolution
        # A changed address has proved nothing, so it stops being confirmed and
        # the learner cannot sign in until the new link is followed. The cached
        # copy has to carry it or the list badge stays stale after a save.
        emailPending
      }
      errors
      # True only when the address changed and a confirmation went out.
      emailed
    }
  }
`

export const APPLY_SKILL_SETUP = gql`
  mutation ApplySkillSetup($studentId: ID!, $statuses: [SkillStatusInput!]!) {
    applySkillSetup(studentId: $studentId, statuses: $statuses) {
      changedCount
      errors
    }
  }
`

export const START_PLACEMENT_TEST = gql`
  mutation StartPlacementTest($studentId: ID!, $subject: String!) {
    startPlacementTest(studentId: $studentId, subject: $subject) {
      test {
        ${PLACEMENT_TEST_FIELDS}
      }
      errors
    }
  }
`

export const ANSWER_PLACEMENT_TEST = gql`
  mutation AnswerPlacementTest($testId: ID!, $questionId: ID!, $answer: String!) {
    answerPlacementTest(testId: $testId, questionId: $questionId, answer: $answer) {
      test {
        ${PLACEMENT_TEST_FIELDS}
      }
      errors
    }
  }
`

export const SET_SUBJECT_PIN = gql`
  mutation SetSubjectPin($studentId: ID!, $subjectId: String!, $state: String) {
    setSubjectPin(studentId: $studentId, subjectId: $subjectId, state: $state) {
      subjects {
        id
        active
        pinned
        hidden
      }
      errors
    }
  }
`

export const SET_SUBJECT_STRANDS = gql`
  mutation SetSubjectStrands($studentId: ID!, $subjectId: String!, $strands: [String!]) {
    setSubjectStrands(studentId: $studentId, subjectId: $subjectId, strands: $strands) {
      subjects {
        id
        strands {
          key
          name
          description
          enabled
          skillCount
        }
      }
      errors
    }
  }
`

export const DELETE_STUDENT = gql`
  mutation DeleteStudent($id: ID!) {
    deleteStudent(id: $id) {
      success
    }
  }
`

export const GENERATE_AVATAR = gql`
  mutation GenerateAvatar($studentId: ID!, $style: String) {
    generateAvatar(studentId: $studentId, style: $style) {
      avatar {
        id
        url
        isActive
      }
      errors
    }
  }
`

export const DELETE_AVATAR = gql`
  mutation DeleteAvatar($id: ID!) {
    deleteAvatar(id: $id) {
      success
      errors
    }
  }
`

export const ACTIVATE_AVATAR = gql`
  mutation ActivateAvatar($id: ID!) {
    activateAvatar(id: $id) {
      avatar {
        id
        url
        isActive
      }
      errors
    }
  }
`

export const UPDATE_PROFICIENCY = gql`
  mutation UpdateProficiency(
    $id: ID!
    $proficiency: Float
    $isUnlocked: Boolean
    $isPriority: Boolean
  ) {
    updateProficiency(
      id: $id
      proficiency: $proficiency
      isUnlocked: $isUnlocked
      isPriority: $isPriority
    ) {
      proficiencyRecord {
        id
        proficiency
        isUnlocked
        isPriority
      }
      errors
    }
  }
`

export const BULK_UPDATE_PROFICIENCIES = gql`
  mutation BulkUpdateProficiencies($updates: [ProficiencyUpdateInput!]!) {
    bulkUpdateProficiencies(updates: $updates) {
      proficiencyRecords {
        id
        proficiency
        isUnlocked
        isPriority
      }
      errors
    }
  }
`

/** A fresh Stripe Customer Portal link — `cancel` opens on cancelling. */
export const OPEN_BILLING_PORTAL = gql`
  mutation OpenBillingPortal($cancel: Boolean, $studentId: ID) {
    openBillingPortal(cancel: $cancel, studentId: $studentId) {
      url
      errors
    }
  }
`

export const START_CHECKOUT = gql`
  mutation StartCheckout($plan: String!, $studentId: ID, $consentVersion: String) {
    startCheckout(plan: $plan, studentId: $studentId, consentVersion: $consentVersion) {
      url
      errors
    }
  }
`
